/**
 * Per-route MobX store for the v2 Provisional Forecast Edit screen.
 *
 * Why a per-route store (and not the global ProvisionalForecastV2Store)
 * ---------------------------------------------------------------------
 * The global store holds short-lived navigation state — search query,
 * sidebar selection, recent concepts, the NDC universe of the
 * currently-clicked concept. The moment a user picks a different
 * concept, that whole shape resets.
 *
 * The Edit screen, by contrast, is a separate route mount with its own
 * lifecycle: it loads a forecast batch for a known set of NDCs, holds
 * draft edits, tracks dirtiness per NDC, persists, and tears down. Tying
 * any of that to the long-lived global store risks state leaking across
 * visits ("why does my last edit still show up?") and forces the global
 * store to grow into something it isn't.
 *
 * State model
 * -----------
 *   slicesByNDC[ndc].serverData  — pristine snapshot from the bulk fetch.
 *                                  Never mutated; the comparison baseline
 *                                  for `isDirty(ndc)`.
 *   workingByNDC[ndc]            — editable working copy, lazy-cloned
 *                                  from serverData on first read. All
 *                                  user edits flow into this.
 *
 * `isDirty(ndc)` is a structural compare against serverData — there is
 * no separate "dirty flag" to keep in sync.
 */

import { makeAutoObservable, runInAction, toJS } from 'mobx';
import { toast } from 'react-toastify';

import {
  GetProvisionalForecastForNDCsResponse,
  MarketDataRow,
  ProvisionalForecastDataEntry,
  ProvisionalForecastForNDCData,
  ProvisionalForecastType,
  SaveProvisionalForecastForNDCRequest,
  SelectedPackSize,
} from 'Interfaces/FDPInterfaces/provisionalForecastV2Interfaces';
import { notesInterface } from 'Interfaces/FDPInterfaces/tmsInterfaces';
import { provisionalForecastV2Services } from 'services/fdp/provisionalForecastV2Services';

const NOTES_PLAN_TYPE = 'provisional';

/**
 * Forecast percentages are bounded to [0, 100] and stored at 2 decimal
 * places (matching the backend's DECIMAL(5,2) column). Clamping AND
 * quantizing at the store keeps every caller (MAS grid, NC grid,
 * future bulk-apply actions) honest with one rule, and ensures the
 * working copy never carries more precision than the column accepts.
 *
 * Non-finite input (NaN from a failed parse, undefined) collapses to
 * 0; the UI catches this case earlier but the defensive coerce keeps
 * the working copy free of garbage numbers.
 */
function clampPercentage(value: number): number {
  if (!Number.isFinite(value)) return 0;
  const bounded = Math.max(0, Math.min(100, value));
  // Round to 2 dp — Math.round avoids float artefacts like
  // 50.00000000001 that JSON parsers occasionally re-emit.
  return Math.round(bounded * 100) / 100;
}

// --------------------------------------------------------------------------- //
// Outer-tab order — fixed per product spec.                                   //
// Tabs whose NDC list is empty are hidden by the consumer.                    //
// --------------------------------------------------------------------------- //
export const OUTER_TAB_ORDER: ProvisionalForecastType[] = ['MAS', 'NC', 'PackoutMAS', 'PackoutNC'];

export const OUTER_TAB_LABELS: Record<ProvisionalForecastType, string> = {
  MAS: 'MAS',
  NC: 'NC',
  PackoutMAS: 'Packout MAS',
  PackoutNC: 'Packout NC',
};

const NC_TYPES: ReadonlySet<ProvisionalForecastType> = new Set(['NC', 'PackoutNC']);

// Mirrors backend `NC_DOD_FACILITY_COTS` in
// PLM_Backend/plm_backend/fdp_app/utilities/provisional_forecast_builders.py.
// Every FacilityCOT in AnnualMarketSales except these two is bucketed
// as VA agency; MTF + TMOP go to DoD.
const NC_DOD_FACILITY_COTS: ReadonlySet<string> = new Set(['MTF', 'TMOP']);
const NC_AGENCY_VA = 'VA';
const NC_AGENCY_DOD = 'DoD';

// Exported so OOC's ProvNC pack-size recompute (oocStore.ts) can bucket
// AnnualMarketSales facilities into the same VA/DoD agency groups as this
// screen — one source of truth for the FacilityCOT → agency rule.
export function ncAgencyForFacilityCot(facilityCot: string): string {
  return NC_DOD_FACILITY_COTS.has(facilityCot) ? NC_AGENCY_DOD : NC_AGENCY_VA;
}

// --------------------------------------------------------------------------- //
// MAS / PackoutMAS per-FacilityCOT edit gate                                  //
// --------------------------------------------------------------------------- //
// The FDPUpdated mock established a "ramp anchor" rule for the 15-month
// MAS grid: each FacilityCOT has a single anchor month; months at or
// before the anchor are editable, months after are read-only and
// auto-propagate from the anchor's value.
//
//   - CMOP-style facilities (Hines IL, VA Facility, every CMOP-prefixed
//     location, basically every FacilityCOT that is NOT in
//     SINGLE_MONTH_ANCHOR_COTS) — months 1–4 editable, month 4 is the
//     anchor that cascades to months 5–15.
//   - MTF / TMOP / VISN — only month 1 is editable. Month 1 is the
//     anchor and cascades to months 2–15. (The mock had a bug where
//     months 2–4 of MTF/VISN rows remained editable but never re-
//     cascaded; we lock them per the stated business intent.)
//
// TMOP wasn't in the mock's cascade branch but is the same DoD agency
// as MTF — treating it like MTF here per product confirmation.
const SINGLE_MONTH_ANCHOR_COTS: ReadonlySet<string> = new Set(['MTF', 'TMOP', 'VISN']);

/**
 * Index of the anchor month for a FacilityCOT. Edits at or before
 * this index are accepted; an edit AT this index cascades the value
 * to every later month. Edits BEFORE this index update only their
 * own cell (mock behaviour: months 1–3 on CMOP rows are independent
 * ramp values).
 *
 * Returns 0 for single-month-anchor COTs (MTF / TMOP / VISN), 3 for
 * everything else (CMOP-style).
 */
export function getEditAnchorMonthIdx(facilityCot: string): number {
  return SINGLE_MONTH_ANCHOR_COTS.has(facilityCot) ? 0 : 3;
}

/**
 * Per-NDC slice of the bulk response. Carries everything a grid needs
 * to render the active inner tab without re-resolving anything.
 */
export interface NDCForecastSlice {
  ndc: string;
  /**
   * Resolved Type. Drives:
   *   - which outer tab the NDC sits under
   *   - the forecast horizon (15 vs 3 months)
   *   - whether free-text packs are accepted on save
   */
  type: ProvisionalForecastType;
  /** Pristine bulk-fetch payload for this NDC. Never mutated. */
  serverData: ProvisionalForecastForNDCData;
}

/** Save status per NDC — drives badges + button enable/disable. */
export type SaveStatus =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'success'; at: number }
  | { kind: 'error'; message: string };

export class ProvisionalForecastV2EditStore {
  // --- Loading + error state ---------------------------------------------- //
  isLoading: boolean = false;
  loadError: string | null = null;

  // --- Bulk response (only valid once isLoading flips back to false) ------ //
  gfc: number | null = null;
  marketData: MarketDataRow[] = [];
  slicesByNDC: Map<string, NDCForecastSlice> = new Map();

  // --- Working copies (editable). Lazy-initialised per NDC on first edit. //
  workingByNDC: Map<string, ProvisionalForecastForNDCData> = new Map();

  // --- Save state per NDC + global save state ------------------------------ //
  saveStatusByNDC: Map<string, SaveStatus> = new Map();
  isSavingAll: boolean = false;
  saveAllError: string | null = null;

  // --- Per-NDC notes (FDP_Notes / NDC11Notes, PlanType=provisional) -------- //
  // notesByNDC: server-side persisted notes for each NDC. Seeded by the
  //   bulk fetch on mount; mutated as the operator adds notes.
  // notesAddedThisSessionByNDC: counter of notes the operator has
  //   ADDED during this Edit mount. Compared against
  //   lastSaveNoteCountByNDC to determine "is there a fresh note since
  //   the last save?" (the save-gate rule).
  // lastSaveNoteCountByNDC: per-NDC watermark — the
  //   notesAddedThisSessionByNDC value at the time of the last
  //   successful save. Resets to 0 on Cancel so a cancelled-then-edited
  //   NDC needs a fresh note.
  notesByNDC: Map<string, notesInterface[]> = new Map();
  isNotesLoading: boolean = false;
  notesLoadError: string | null = null;
  isNoteSavingByNDC: Map<string, boolean> = new Map();
  noteSaveErrorByNDC: Map<string, string> = new Map();
  notesAddedThisSessionByNDC: Map<string, number> = new Map();
  lastSaveNoteCountByNDC: Map<string, number> = new Map();

  // --- Tab state ---------------------------------------------------------- //
  activeOuterTab: ProvisionalForecastType | null = null;
  activeInnerNDCByTab: Partial<Record<ProvisionalForecastType, string>> = {};

  // Variance flag threshold is now persisted per-NDC on the backend
  // (FCT_ProvisionalForecast.VarianceFlagThreshold, see migration
  // 0015). The store reads it from the active slice's working copy
  // (or the server snapshot when nothing has been edited yet) and
  // writes back through the working copy so the change rides along on
  // the next save. See `varianceThreshold` getter + setter below.

  constructor() {
    makeAutoObservable(this);
  }

  // ------------------------------------------------------------------------ //
  // Derived views                                                             //
  // ------------------------------------------------------------------------ //
  get outerTabs(): { type: ProvisionalForecastType; ndcs: string[] }[] {
    const byType: Record<ProvisionalForecastType, string[]> = {
      MAS: [],
      NC: [],
      PackoutMAS: [],
      PackoutNC: [],
    };
    for (const slice of this.slicesByNDC.values()) {
      byType[slice.type].push(slice.ndc);
    }
    return OUTER_TAB_ORDER.filter(t => byType[t].length > 0).map(type => ({
      type,
      ndcs: byType[type],
    }));
  }

  get activeSlice(): NDCForecastSlice | null {
    if (!this.activeOuterTab) return null;
    const ndc = this.activeInnerNDCByTab[this.activeOuterTab];
    if (!ndc) return null;
    return this.slicesByNDC.get(ndc) ?? null;
  }

  // ------------------------------------------------------------------------ //
  // Working-copy access                                                      //
  // ------------------------------------------------------------------------ //
  /**
   * Returns the editable working copy for `ndc`, lazy-cloning from the
   * pristine server snapshot on first call. Subsequent calls return the
   * same observable object so input components can two-way bind to it.
   */
  getWorking = (ndc: string): ProvisionalForecastForNDCData | null => {
    const existing = this.workingByNDC.get(ndc);
    if (existing) return existing;
    const slice = this.slicesByNDC.get(ndc);
    if (!slice) return null;
    const clone = cloneNDCData(slice.serverData);
    // Critical: when we `.set` a plain object into MobX's observable
    // Map, MobX deep-wraps it on insertion. The wrapped proxy is what
    // `.get` returns afterwards — NOT the original `clone` reference.
    // Returning the original would silently bypass observation: caller
    // mutations on the plain object never fire reactions, and the UI
    // sees the change only after some other observable nudges it
    // (manifests as "first click looks swallowed"). Read back from
    // the Map so callers always get the proxy.
    runInAction(() => {
      this.workingByNDC.set(ndc, clone);
    });
    return this.workingByNDC.get(ndc) ?? clone;
  };

  /**
   * Structural compare of the working copy against the pristine snapshot.
   * Cheap because we only run it for the active NDC during render.
   */
  isDirty = (ndc: string): boolean => {
    const working = this.workingByNDC.get(ndc);
    if (!working) return false;
    const slice = this.slicesByNDC.get(ndc);
    if (!slice) return false;
    return !ndcDataEquals(working, slice.serverData);
  };

  /** Any NDC in the batch with unsaved edits. */
  get anyDirty(): boolean {
    for (const ndc of this.workingByNDC.keys()) {
      if (this.isDirty(ndc)) return true;
    }
    return false;
  }

  /**
   * Per-row recomputed totals for the active NDC, derived from the
   * currently-calc packs (i.e. `used_in_calc=true` in the working
   * copy if present, else the server snapshot).
   *
   * The shape returned mirrors `entry.data[]` keys so callers can
   * look up by `facility_cot_agency`:
   *
   *   - MAS / PackoutMAS: keys are FacilityCOT strings ("TMOP", "VA Facility", ...)
   *     and we sum market_data rows where `facility_cot` matches the key.
   *   - NC / PackoutNC: keys are agency strings ("VA", "DoD") and we
   *     bucket market_data rows via NC_DOD_FACILITY_COTS, summing
   *     within each bucket.
   *
   * `eu` is included only for MAS — it equals `annual_market` per
   * product spec. For NC, `eu` is operator-edited (Annual NCE) and
   * must NOT be overwritten by the recompute. The caller skips the
   * `eu` write on NC types.
   */
  recomputedFacilityTotals = (ndc: string): Map<string, { annual_market: number; eu: number }> => {
    const out = new Map<string, { annual_market: number; eu: number }>();
    const slice = this.slicesByNDC.get(ndc);
    if (!slice) return out;
    const data = this.workingByNDC.get(ndc) ?? slice.serverData;
    const isNC = NC_TYPES.has(slice.type);

    const calcPacks = new Set(
      data.selected_packsizes.filter(p => p.selected && p.used_in_calc).map(p => p.packsize)
    );

    if (isNC) {
      // Bucket market_data into VA / DoD totals first, then project
      // onto each NC `data` entry by its agency name. We don't assume
      // both buckets are present in `data` (Case C only emits buckets
      // that have at least one underlying market row).
      const agencySums: Record<string, number> = {
        [NC_AGENCY_VA]: 0,
        [NC_AGENCY_DOD]: 0,
      };
      for (const row of this.marketData) {
        if (!calcPacks.has(row.packsize)) continue;
        const agency = ncAgencyForFacilityCot(row.facility_cot);
        const v = parseFloat(row.annual_market);
        if (Number.isFinite(v)) agencySums[agency] += v;
      }
      for (const entry of data.data) {
        const annualMarketInt = Math.floor(agencySums[entry.facility_cot_agency] ?? 0);
        out.set(entry.facility_cot_agency, {
          annual_market: annualMarketInt,
          // eu intentionally echoed; the apply-helper drops it for NC.
          eu: entry.eu,
        });
      }
      return out;
    }

    // MAS / PackoutMAS: facility-COT-keyed sum.
    for (const entry of data.data) {
      let annualMarket = 0;
      for (const row of this.marketData) {
        if (row.facility_cot !== entry.facility_cot_agency) continue;
        if (!calcPacks.has(row.packsize)) continue;
        const v = parseFloat(row.annual_market);
        if (Number.isFinite(v)) annualMarket += v;
      }
      const annualMarketInt = Math.floor(annualMarket);
      out.set(entry.facility_cot_agency, {
        annual_market: annualMarketInt,
        eu: annualMarketInt, // per product spec — eu mirrors annual_market
      });
    }
    return out;
  };

  // ------------------------------------------------------------------------ //
  // Edit actions                                                              //
  // ------------------------------------------------------------------------ //
  /**
   * MAS / PackoutMAS edit + anchor cascade. The 15-month grid has a
   * per-FacilityCOT "anchor" month — see `getEditAnchorMonthIdx`.
   *
   * Edit semantics:
   *   - An edit AT the anchor month cascades: every month STRICTLY
   *     AFTER the anchor is overwritten with the same value. Months
   *     before the anchor are left untouched (CMOP rows treat them as
   *     independent ramp values).
   *   - An edit at any OTHER month (before or after the anchor)
   *     updates only that single cell — no cascade.
   *
   * All months are user-editable; the cascade is the only ambient
   * behaviour that distinguishes the anchor month from the rest.
   *
   * Should NOT be used by the NC grid — NC has its own
   * `setSharedMonthPercentage` flow (no anchor concept on 3 months).
   */
  setMonthPercentageWithCascade = (
    ndc: string,
    rowIdx: number,
    monthIdx: number,
    value: number
  ) => {
    const working = this.getWorking(ndc);
    if (!working) return;
    const row = working.data[rowIdx];
    if (!row) return;
    const anchor = getEditAnchorMonthIdx(row.facility_cot_agency);
    const month = row.forecast_data[monthIdx];
    if (!month) return;
    const clamped = clampPercentage(value);
    runInAction(() => {
      month.forecast_percentage = clamped;
      if (monthIdx === anchor) {
        // Cascade: overwrite every month strictly after the anchor
        // with the same value. Months before the anchor (only relevant
        // for CMOP rows) are left untouched — they're independent ramp
        // values.
        for (let i = anchor + 1; i < row.forecast_data.length; i++) {
          const later = row.forecast_data[i];
          if (later) later.forecast_percentage = clamped;
        }
      }
    });
  };

  /**
   * NC variant — writes the same percentage to every agency row's
   * `forecast_data[monthIdx]`. The mock NC view
   * exposes a single "% Applied" control per month rather than a
   * per-agency editor; this action keeps every agency row in lockstep
   * so the saved payload reflects what the operator sees.
   */
  setSharedMonthPercentage = (ndc: string, monthIdx: number, value: number) => {
    const working = this.getWorking(ndc);
    if (!working) return;
    const clamped = clampPercentage(value);
    runInAction(() => {
      for (const row of working.data) {
        const m = row.forecast_data[monthIdx];
        if (m) m.forecast_percentage = clamped;
      }
    });
  };

  /**
   * NC: sets Annual NCE (the editable `eu` column) for one agency
   * row. This is operator-edited and is what drives the variance
   * computation in the NC variance/forecast table.
   */
  setAgencyEU = (ndc: string, rowIdx: number, value: number) => {
    const working = this.getWorking(ndc);
    if (!working) return;
    const row = working.data[rowIdx];
    if (!row) return;
    runInAction(() => {
      row.eu = value;
    });
  };

  /**
   * Toggle a single row's `is_facility_selected` flag on the working
   * copy. Pure UI state: forecast values, annual_market, eu, and the
   * row's persistence are all untouched. The grid is responsible for
   * excluding unchecked rows from subtotals / TOTAL.
   *
   * No-op on bad indices (defensive against grid race conditions if
   * the slice changes underneath an open checkbox).
   */
  setFacilitySelected = (ndc: string, rowIdx: number, on: boolean) => {
    const working = this.getWorking(ndc);
    if (!working) return;
    const row = working.data[rowIdx];
    if (!row) return;
    runInAction(() => {
      row.is_facility_selected = on;
    });
  };

  /**
   * Variance flag threshold for the *active* NDC. Reads the working
   * copy when present (so an in-flight edit shows immediately), else
   * the pristine server snapshot, else the hard-coded fallback of 15
   * that matches the backend default.
   */
  get varianceThreshold(): number {
    const slice = this.activeSlice;
    if (!slice) return 15;
    const working = this.workingByNDC.get(slice.ndc);
    const source = working ?? slice.serverData;
    const raw = source.variance_flag_threshold;
    if (typeof raw !== 'number' || !Number.isFinite(raw)) return 15;
    return raw;
  }

  /**
   * Update the variance flag threshold for the active NDC. Writes
   * through the working copy so the value rides along on the next
   * save and persists on the backend's `VarianceFlagThreshold` column.
   * Clamped to `[1, VARIANCE_THRESHOLD_HARD_MAX]` to mirror the
   * backend validator — see the constant for the rationale on why
   * there's no tight upper cap.
   */
  setVarianceThreshold = (value: number) => {
    const slice = this.activeSlice;
    if (!slice) return;
    const clamped = Math.max(1, Math.min(VARIANCE_THRESHOLD_HARD_MAX, Math.floor(value)));
    const working = this.getWorking(slice.ndc);
    if (!working) return;
    runInAction(() => {
      working.variance_flag_threshold = clamped;
    });
  };

  /**
   * Toggles a packsize's `selected` flag. For non-Packout types we keep
   * `used_in_calc` mirrored to `selected` (Q19 default). For Packout we
   * leave `used_in_calc` alone — the dual-selector lands in a later
   * task and will set it explicitly.
   *
   * After flipping the flag we re-derive `annual_market` and `eu` per
   * facility from market_data so the saved payload matches what the
   * grid is actually displaying. The backend trusts these values
   * verbatim (they're not server-recomputed on save), so failing to
   * keep them in sync would leak stale numbers into FCT_ProvisionalForecast.
   */
  /**
   * Plain MAS / NC chip: one flag drives both `selected` and
   * `used_in_calc` (they're always mirrored for these Types). The
   * recompute fires on every flip.
   *
   * Packout uses `setPackSelected` + `setCalcPacks` separately —
   * never call this method on a Packout NDC.
   *
   * Refuses to uncheck the last selected pack — leaving every
   * `used_in_calc=false` would zero out the math and produce a
   * save body the operator never intended. Operator gets a toast
   * and the click is a no-op.
   */
  togglePackSelected = (ndc: string, packsize: string) => {
    const working = this.getWorking(ndc);
    if (!working) return;
    const list = working.selected_packsizes;
    const existing = list.find(p => p.packsize === packsize);
    const isUnchecking = existing?.used_in_calc === true;
    if (isUnchecking) {
      const otherSelectedCount = list.filter(p => p.packsize !== packsize && p.used_in_calc).length;
      if (otherSelectedCount === 0) {
        toast.warn('At least one pack size must remain selected.');
        return;
      }
    }
    runInAction(() => {
      if (existing) {
        const nextOn = !existing.used_in_calc;
        existing.used_in_calc = nextOn;
        existing.selected = nextOn;
      } else {
        list.push({ packsize, selected: true, used_in_calc: true });
      }
      this.applyRecomputedTotalsToWorking(ndc);
    });
  };

  /**
   * Packout: flip the SAVED set (`selected`) for a pack. Toggling
   * `selected` here NEVER touches `used_in_calc` and NEVER triggers
   * a recompute — left-chip control is independent of the Calc
   * dropdown by product spec.
   *
   * Refuses to uncheck the last selected view-pack. The view-pack
   * list IS what the operator is recording as "packed out"; an empty
   * list is never a valid state. Operator gets a toast and the click
   * is a no-op.
   *
   * The backend invariant `used_in_calc ⇒ selected` is enforced at
   * save-payload-construction time (see `toSavePayload`), not here —
   * this keeps the two controls orthogonal in the UI while still
   * producing a valid save body.
   */
  setPackSelected = (ndc: string, packsize: string, on: boolean) => {
    const working = this.getWorking(ndc);
    if (!working) return;
    const list = working.selected_packsizes;
    const existing = list.find(p => p.packsize === packsize);
    if (!on && existing?.selected === true) {
      const otherSelectedCount = list.filter(p => p.packsize !== packsize && p.selected).length;
      if (otherSelectedCount === 0) {
        toast.warn('At least one pack size must remain selected.');
        return;
      }
    }
    runInAction(() => {
      if (existing) {
        existing.selected = on;
      } else if (on) {
        // Pack appeared in market_data but wasn't in the saved list yet.
        list.push({ packsize, selected: true, used_in_calc: false });
      }
    });
  };

  /**
   * Packout: replace the calc set wholesale. Only packs in this list
   * end up with `used_in_calc=true`; everything else flips off.
   *
   * Strictly does not touch `selected` — left chips and Calc are
   * independent controls. The backend invariant `used_in_calc ⇒
   * selected` is reconciled at save time inside `toSavePayload` (a
   * pack that's calc-on but not selected gets coerced to calc-off).
   */
  setCalcPacks = (ndc: string, packsizes: string[]) => {
    const working = this.getWorking(ndc);
    if (!working) return;
    const target = new Set(packsizes);
    const list = working.selected_packsizes;
    runInAction(() => {
      for (const entry of list) {
        entry.used_in_calc = target.has(entry.packsize);
      }
      // Defensive: a calc pack the operator picked that wasn't on the
      // saved list yet (shouldn't happen — Calc options come from
      // market_data — but harmless to handle). The pack lands as
      // calc-on, selected-off; toSavePayload normalises if not
      // checked on the left before save.
      for (const ps of packsizes) {
        if (!list.find(p => p.packsize === ps)) {
          list.push({ packsize: ps, selected: false, used_in_calc: true });
        }
      }
      this.applyRecomputedTotalsToWorking(ndc);
    });
  };

  /**
   * Packout-only: append a user-typed free-text pack to the saved
   * set. New packs land with `selected=true, used_in_calc=false` so
   * they participate in the saved record but don't affect the math
   * until the operator adds them to the Calc selector.
   *
   * No-op if the pack already exists (deduped on raw string match —
   * Decimal-normalized comparison would mean rejecting "100" when
   * "100.0" exists, which is too strict for free-text input).
   */
  addUserPackSize = (ndc: string, packsize: string): boolean => {
    const trimmed = packsize.trim();
    if (!trimmed) return false;
    const working = this.getWorking(ndc);
    if (!working) return false;
    const exists = working.selected_packsizes.some(p => p.packsize === trimmed);
    if (exists) return false;
    runInAction(() => {
      working.selected_packsizes.push({
        packsize: trimmed,
        selected: true,
        used_in_calc: false,
      });
    });
    return true;
  };

  /**
   * Re-derives per-row `annual_market` (and for MAS, `eu`) on the
   * working copy from the currently calc-selected packs and persists
   * them back into `working.data[]`. Called after every pack toggle so
   * the SAVE payload (which the backend stores verbatim) stays in
   * sync with the grid's displayed numbers.
   *
   * For NC types, `eu` is operator-edited (Annual NCE in the variance
   * panel) and must NOT be overwritten when the calc set changes —
   * we only update `annual_market`.
   *
   * Edge case: if zero packs are calc'd (Packout's first-load state),
   * we leave the values alone. The server returned a sensible initial
   * Annual/EU; clobbering it with zeros would visually break the grid
   * before the operator has had a chance to opt-in to a calc set.
   */
  private applyRecomputedTotalsToWorking = (ndc: string) => {
    const working = this.workingByNDC.get(ndc);
    if (!working) return;
    const calcCount = working.selected_packsizes.filter(p => p.selected && p.used_in_calc).length;
    if (calcCount === 0) return;
    const isNC = NC_TYPES.has(working.type);
    const totals = this.recomputedFacilityTotals(ndc);
    for (const entry of working.data) {
      const t = totals.get(entry.facility_cot_agency);
      if (!t) continue;
      entry.annual_market = t.annual_market;
      if (!isNC) entry.eu = t.eu;
    }
  };

  // ------------------------------------------------------------------------ //
  // Loading                                                                  //
  // ------------------------------------------------------------------------ //
  loadForNDCs = async (ndcs: string[]): Promise<void> => {
    runInAction(() => {
      this.isLoading = true;
      this.loadError = null;
      this.gfc = null;
      this.marketData = [];
      this.slicesByNDC = new Map();
      this.workingByNDC = new Map();
      this.saveStatusByNDC = new Map();
      this.isSavingAll = false;
      this.saveAllError = null;
      this.activeOuterTab = null;
      this.activeInnerNDCByTab = {};
      // Notes state is a session concept — wipe on every load.
      this.notesByNDC = new Map();
      this.isNotesLoading = false;
      this.notesLoadError = null;
      this.isNoteSavingByNDC = new Map();
      this.noteSaveErrorByNDC = new Map();
      this.notesAddedThisSessionByNDC = new Map();
      this.lastSaveNoteCountByNDC = new Map();
    });

    if (ndcs.length === 0) {
      runInAction(() => {
        this.isLoading = false;
        this.loadError = 'No NDCs supplied to load.';
      });
      return;
    }

    let response: GetProvisionalForecastForNDCsResponse;
    try {
      response = await provisionalForecastV2Services.getProvisionalForecastForNDCs({
        ndcs,
      });
    } catch (err) {
      const message = this.extractErrorMessage(err) ?? 'Could not load forecasts.';
      runInAction(() => {
        this.isLoading = false;
        this.loadError = message;
      });
      console.error('ProvisionalForecastV2EditStore.loadForNDCs error', err);
      return;
    }

    runInAction(() => {
      this.gfc = response.gfc;
      this.marketData = response.market_data ?? [];
      const map = new Map<string, NDCForecastSlice>();
      for (const block of response.provisional_data ?? []) {
        map.set(block.ndc, {
          ndc: block.ndc,
          type: block.type,
          serverData: block,
        });
      }
      this.slicesByNDC = map;

      const firstTab = this.outerTabs[0];
      if (firstTab) {
        this.activeOuterTab = firstTab.type;
        this.activeInnerNDCByTab = {
          ...this.activeInnerNDCByTab,
          [firstTab.type]: firstTab.ndcs[0],
        };
      }
      this.isLoading = false;
    });

    // Notes prefetch is no longer kicked off here — the Edit screen
    // fires it in parallel with this method (both consume the same
    // URL ?ndcs= list). Keeping them independent lets the notes
    // round-trip start at frame zero instead of waiting for the
    // forecast response to land.
  };

  /**
   * Bulk-fetch the active notes for the given NDCs and seed
   * `notesByNDC` so the per-tab Notes panel can render immediately
   * on first tab visit.
   *
   * Called by the Edit screen on mount, in parallel with
   * `loadForNDCs`. Both calls take the same URL `?ndcs=` list, so
   * coupling them sequentially would only add latency.
   *
   * Errors are recorded on `notesLoadError` but do NOT block the
   * forecast grid — notes are auxiliary to the edit flow. Dedupes
   * + trims input defensively in case the caller passes raw URL
   * fragments.
   */
  prefetchNotes = async (ndcs: string[]): Promise<void> => {
    // Defensive trim + dedupe — caller is the Edit component pulling
    // straight from the URL `?ndcs=` param, so values may have
    // whitespace artifacts from a hand-edited link.
    const seen = new Set<string>();
    const cleaned: string[] = [];
    for (const raw of ndcs) {
      if (typeof raw !== 'string') continue;
      const trimmed = raw.trim();
      if (!trimmed || seen.has(trimmed)) continue;
      seen.add(trimmed);
      cleaned.push(trimmed);
    }
    if (cleaned.length === 0) return;

    runInAction(() => {
      this.isNotesLoading = true;
      this.notesLoadError = null;
    });
    try {
      const response = await provisionalForecastV2Services.getNDC11NotesForNDCs({
        ndcs: cleaned,
        planning_type: NOTES_PLAN_TYPE,
      });
      runInAction(() => {
        const map = new Map<string, notesInterface[]>();
        for (const ndc of cleaned) {
          map.set(ndc, response.notes_by_ndc[ndc] ?? []);
        }
        this.notesByNDC = map;
        this.isNotesLoading = false;
      });
    } catch (err) {
      const message = this.extractErrorMessage(err) ?? 'Could not load notes for these NDCs.';
      runInAction(() => {
        this.isNotesLoading = false;
        this.notesLoadError = message;
        // Seed empty arrays so per-tab panels still render an empty
        // state rather than spinning forever.
        const map = new Map<string, notesInterface[]>();
        for (const ndc of cleaned) map.set(ndc, []);
        this.notesByNDC = map;
      });
      console.error('ProvisionalForecastV2EditStore.prefetchNotes error', err);
    }
  };

  /**
   * Adds one note to `ndc`'s active list. On success:
   *   - Replaces the per-NDC notes cache with the post-insert list
   *     returned by the API (so order/IDs match the server).
   *   - Increments the per-NDC session counter — this is what the
   *     save-gate compares against `lastSaveNoteCountByNDC[ndc]`.
   *
   * Returns true on success, false on failure (error stamped on
   * `noteSaveErrorByNDC`).
   */
  addNoteFor = async (ndc: string, note: string): Promise<boolean> => {
    const trimmed = note.trim();
    if (!trimmed) return false;
    runInAction(() => {
      this.isNoteSavingByNDC.set(ndc, true);
      this.noteSaveErrorByNDC.delete(ndc);
    });
    try {
      const response = await provisionalForecastV2Services.saveNDC11Note({
        ndc11: ndc,
        note: trimmed,
        planning_type: NOTES_PLAN_TYPE,
      });
      runInAction(() => {
        this.notesByNDC.set(ndc, response.data ?? []);
        // Advance session counter — this clears the save-gate debt
        // for this NDC if it was dirty without a fresh note.
        const next = (this.notesAddedThisSessionByNDC.get(ndc) ?? 0) + 1;
        this.notesAddedThisSessionByNDC.set(ndc, next);
        this.isNoteSavingByNDC.set(ndc, false);
      });
      return true;
    } catch (err) {
      const message = this.extractErrorMessage(err) ?? 'Could not save the note.';
      runInAction(() => {
        this.isNoteSavingByNDC.set(ndc, false);
        this.noteSaveErrorByNDC.set(ndc, message);
      });
      console.error('ProvisionalForecastV2EditStore.addNoteFor error', err);
      return false;
    }
  };

  /**
   * Save gate stub — always returns `false` (no gating).
   *
   * Historical context: an earlier iteration required a fresh note
   * before saving any dirty NDC. That gate was removed per product
   * decision; notes are now strictly optional. The method is kept on
   * the store so callers (SaveBar, saveOne, saveAllDirty) compile
   * unchanged and so re-introducing a gate later is a one-line change.
   */
  requiresNoteForSave = (_ndc: string): boolean => {
    return false;
  };

  /** Always empty — see `requiresNoteForSave` for the deprecation note. */
  get ndcsBlockedByNoteGate(): string[] {
    return [];
  }

  // ------------------------------------------------------------------------ //
  // Tab navigation                                                           //
  // ------------------------------------------------------------------------ //
  selectOuterTab = (type: ProvisionalForecastType) => {
    runInAction(() => {
      this.activeOuterTab = type;
      if (!this.activeInnerNDCByTab[type]) {
        const tab = this.outerTabs.find(t => t.type === type);
        if (tab && tab.ndcs.length > 0) {
          this.activeInnerNDCByTab = {
            ...this.activeInnerNDCByTab,
            [type]: tab.ndcs[0],
          };
        }
      }
    });
  };

  selectInnerNDC = (type: ProvisionalForecastType, ndc: string) => {
    runInAction(() => {
      this.activeInnerNDCByTab = {
        ...this.activeInnerNDCByTab,
        [type]: ndc,
      };
    });
  };

  // ------------------------------------------------------------------------ //
  // Save                                                                     //
  // ------------------------------------------------------------------------ //
  /**
   * Cancel: drop the working copy for `ndc` so the grid reverts to
   * server state. Also resets the per-NDC note-debt counters as a
   * housekeeping measure — the counters no longer gate save (notes
   * are optional now), but resetting on Cancel keeps the in-memory
   * state tidy in case the gate is reinstated.
   */
  cancelEdits = (ndc: string) => {
    runInAction(() => {
      this.workingByNDC.delete(ndc);
      this.saveStatusByNDC.set(ndc, { kind: 'idle' });
      this.notesAddedThisSessionByNDC.delete(ndc);
      this.lastSaveNoteCountByNDC.delete(ndc);
    });
  };

  /** Cancel everything: drop every working copy + reset bookkeeping. */
  cancelAllEdits = () => {
    runInAction(() => {
      this.workingByNDC = new Map();
      this.saveStatusByNDC = new Map();
      this.saveAllError = null;
      this.notesAddedThisSessionByNDC = new Map();
      this.lastSaveNoteCountByNDC = new Map();
    });
  };

  saveOne = async (ndc: string): Promise<boolean> => {
    const working = this.workingByNDC.get(ndc);
    if (!working) return false;
    runInAction(() => {
      this.saveStatusByNDC.set(ndc, { kind: 'saving' });
    });
    const payload = toSavePayload(working);
    try {
      await provisionalForecastV2Services.saveProvisionalForecastForNDC(payload);
      runInAction(() => {
        // Promote the working copy to the new pristine snapshot so
        // isDirty flips false and subsequent edits diff against the
        // just-saved server state.
        const slice = this.slicesByNDC.get(ndc);
        if (slice) {
          slice.serverData = cloneNDCData(working);
        }
        this.workingByNDC.delete(ndc);
        this.saveStatusByNDC.set(ndc, { kind: 'success', at: Date.now() });
        // Advance the watermark — the next edit on this NDC will need
        // a fresh note before save (since added <= watermark again).
        this.lastSaveNoteCountByNDC.set(ndc, this.notesAddedThisSessionByNDC.get(ndc) ?? 0);
      });
      toast.success(`Saved forecast for ${ndc}.`);
      return true;
    } catch (err) {
      const message = this.extractErrorMessage(err) ?? 'Save failed.';
      runInAction(() => {
        this.saveStatusByNDC.set(ndc, { kind: 'error', message });
      });
      toast.error(message);
      console.error('ProvisionalForecastV2EditStore.saveOne error', err);
      return false;
    }
  };

  saveAllDirty = async (): Promise<boolean> => {
    const dirtyNDCs: string[] = [];
    for (const ndc of this.workingByNDC.keys()) {
      if (this.isDirty(ndc)) dirtyNDCs.push(ndc);
    }
    if (dirtyNDCs.length === 0) return true;

    runInAction(() => {
      this.isSavingAll = true;
      this.saveAllError = null;
      for (const ndc of dirtyNDCs) {
        this.saveStatusByNDC.set(ndc, { kind: 'saving' });
      }
    });

    const items: SaveProvisionalForecastForNDCRequest[] = dirtyNDCs.map(ndc =>
      toSavePayload(this.workingByNDC.get(ndc)!)
    );

    try {
      await provisionalForecastV2Services.saveProvisionalForecastForNDCs({ items });
      runInAction(() => {
        for (const ndc of dirtyNDCs) {
          const working = this.workingByNDC.get(ndc);
          if (working) {
            const slice = this.slicesByNDC.get(ndc);
            if (slice) slice.serverData = cloneNDCData(working);
            this.workingByNDC.delete(ndc);
          }
          this.saveStatusByNDC.set(ndc, { kind: 'success', at: Date.now() });
          // Watermark: any future edit on this NDC will need a fresh note.
          this.lastSaveNoteCountByNDC.set(ndc, this.notesAddedThisSessionByNDC.get(ndc) ?? 0);
        }
        this.isSavingAll = false;
      });
      toast.success(
        dirtyNDCs.length === 1
          ? `Saved forecast for ${dirtyNDCs[0]}.`
          : `Saved forecasts for ${dirtyNDCs.length} NDCs.`
      );
      return true;
    } catch (err) {
      const errBody = extractFDPErrorBody(err);
      runInAction(() => {
        // Per-item failures: stamp those NDCs with their specific error.
        if (errBody?.errors && errBody.errors.length > 0) {
          for (const item of errBody.errors) {
            if (item.ndc) {
              this.saveStatusByNDC.set(item.ndc, {
                kind: 'error',
                message: item.error,
              });
            }
          }
          // NDCs that didn't get a per-item error roll back to idle —
          // the bulk save was atomic, so they didn't actually save.
          for (const ndc of dirtyNDCs) {
            const errored = errBody.errors.some(e => e.ndc === ndc);
            if (!errored) this.saveStatusByNDC.set(ndc, { kind: 'idle' });
          }
        } else {
          for (const ndc of dirtyNDCs) {
            this.saveStatusByNDC.set(ndc, { kind: 'idle' });
          }
        }
        this.isSavingAll = false;
        this.saveAllError = errBody?.error ?? 'Save All failed.';
      });
      toast.error(errBody?.error ?? 'Save All failed.');
      console.error('ProvisionalForecastV2EditStore.saveAllDirty error', err);
      return false;
    }
  };

  // ------------------------------------------------------------------------ //
  // Cleanup                                                                  //
  // ------------------------------------------------------------------------ //
  reset = () => {
    runInAction(() => {
      this.isLoading = false;
      this.loadError = null;
      this.gfc = null;
      this.marketData = [];
      this.slicesByNDC = new Map();
      this.workingByNDC = new Map();
      this.saveStatusByNDC = new Map();
      this.isSavingAll = false;
      this.saveAllError = null;
      this.activeOuterTab = null;
      this.activeInnerNDCByTab = {};
      this.notesByNDC = new Map();
      this.isNotesLoading = false;
      this.notesLoadError = null;
      this.isNoteSavingByNDC = new Map();
      this.noteSaveErrorByNDC = new Map();
      this.notesAddedThisSessionByNDC = new Map();
      this.lastSaveNoteCountByNDC = new Map();
    });
  };

  // ------------------------------------------------------------------------ //
  // Helpers                                                                  //
  // ------------------------------------------------------------------------ //
  private extractErrorMessage = (err: unknown): string | null => {
    if (err && typeof err === 'object' && 'response' in err) {
      const resp = (err as { response?: { data?: { error?: string } } }).response;
      if (resp?.data?.error) return resp.data.error;
    }
    return null;
  };
}

// --------------------------------------------------------------------------- //
// Pure helpers (no observable state)                                          //
// --------------------------------------------------------------------------- //
/**
 * Deep-clones an NDC payload so the working copy can be mutated without
 * touching the pristine snapshot. JSON round-trip is fine here: the
 * payload is plain data (numbers, strings, booleans, arrays).
 */
function cloneNDCData(d: ProvisionalForecastForNDCData): ProvisionalForecastForNDCData {
  return JSON.parse(JSON.stringify(toJS(d)));
}

/** Structural equality on the editable subset of an NDC payload. */
function ndcDataEquals(
  a: ProvisionalForecastForNDCData,
  b: ProvisionalForecastForNDCData
): boolean {
  if (a.ndc !== b.ndc) return false;
  if (a.type !== b.type) return false;
  // Threshold edit alone is a real edit — it round-trips to the
  // VarianceFlagThreshold column on save.
  if (a.variance_flag_threshold !== b.variance_flag_threshold) return false;
  if (!packListEquals(a.selected_packsizes, b.selected_packsizes)) return false;
  if (a.data.length !== b.data.length) return false;
  for (let i = 0; i < a.data.length; i++) {
    if (!entryEquals(a.data[i], b.data[i])) return false;
  }
  return true;
}

function entryEquals(a: ProvisionalForecastDataEntry, b: ProvisionalForecastDataEntry): boolean {
  if (a.facility_cot_agency !== b.facility_cot_agency) return false;
  if (a.annual_market !== b.annual_market) return false;
  if (a.eu !== b.eu) return false;
  // is_facility_selected is part of the editable surface — toggling
  // the checkbox is a real edit that should mark the NDC dirty.
  if (a.is_facility_selected !== b.is_facility_selected) return false;
  if (a.forecast_data.length !== b.forecast_data.length) return false;
  for (let i = 0; i < a.forecast_data.length; i++) {
    const x = a.forecast_data[i];
    const y = b.forecast_data[i];
    if (x.forecast_month !== y.forecast_month) return false;
    if (x.forecast_percentage !== y.forecast_percentage) return false;
  }
  return true;
}

function packListEquals(a: SelectedPackSize[], b: SelectedPackSize[]): boolean {
  if (a.length !== b.length) return false;
  // Order matters because the backend persists the list as-is; treating
  // re-order as a no-op would silently drop a legitimate edit.
  for (let i = 0; i < a.length; i++) {
    if (a[i].packsize !== b[i].packsize) return false;
    if (a[i].selected !== b[i].selected) return false;
    if (a[i].used_in_calc !== b[i].used_in_calc) return false;
  }
  return true;
}

/** Strip the server-resolved `type` field on the way out — the SAVE
 *  contract is decoupled from the GET shape (interfaces file Q-B5).
 *
 *  Also enforces the backend invariant `used_in_calc ⇒ selected` here,
 *  not in the edit actions: the left chips and Calc dropdown are
 *  orthogonal in the UI, so it's possible to land in a working-copy
 *  state where a pack is `used_in_calc=true, selected=false`. The
 *  validator rejects that, so we coerce `used_in_calc=false` for any
 *  pack that's not selected before sending. */
function toSavePayload(d: ProvisionalForecastForNDCData): SaveProvisionalForecastForNDCRequest {
  const plain = toJS(d);
  return {
    ndc: plain.ndc,
    // Clamp on the way out to mirror the backend validator's bounds.
    // Missing/non-numeric values from a malformed working copy fall
    // back to 15 so a save never fails on this field alone.
    variance_flag_threshold: clampVarianceThreshold(plain.variance_flag_threshold),
    selected_packsizes: plain.selected_packsizes.map(p => ({
      packsize: p.packsize,
      selected: p.selected,
      used_in_calc: p.selected ? p.used_in_calc : false,
    })),
    data: plain.data,
  };
}

function clampVarianceThreshold(value: unknown): number {
  const n = typeof value === 'number' && Number.isFinite(value) ? value : 15;
  return Math.max(1, Math.min(VARIANCE_THRESHOLD_HARD_MAX, Math.floor(n)));
}

/**
 * Hard upper bound on the variance flag threshold percentage.
 *
 * Product intent is "no effective cap" — operators may legitimately
 * dial very high thresholds (e.g. 250 %) to effectively suppress
 * flagging on a high-variance forecast. The number below is purely a
 * sanity guardrail so a stray paste of `1e9` can't slip past the
 * frontend and overflow downstream arithmetic; SQL Server `INT` has
 * plenty of room beyond this. Mirrors the backend constant in
 * `fdp_app/utilities/provisional_forecast_save_validators.py`.
 */
const VARIANCE_THRESHOLD_HARD_MAX = 100000;

interface FDPErrorBodyShape {
  error?: string;
  errors?: { index: number; ndc: string | null; error: string }[];
}

function extractFDPErrorBody(err: unknown): FDPErrorBodyShape | null {
  if (err && typeof err === 'object' && 'response' in err) {
    const resp = (err as { response?: { data?: FDPErrorBodyShape } }).response;
    if (resp?.data) return resp.data;
  }
  return null;
}
